import { ReactNode } from 'react';
import moment from 'moment';
import { Popup, PopupBody, PopupFooter } from '../../controls';
import { constants } from '../../../constants';
import { DateTimeStamp } from '../../../types/primitives/DateTimeStamp';

export interface LenderCallPopupProps {
    issuer: string;
    lenderCallDate: DateTimeStamp;
    liveDomestic?: string;
    liveInternational?: string;
    livePasscode?: string;
    replayDomestic?: string;
    replayInternational?: string;
    replayPasscode?: string;
    onClose: () => void;
}

export function LenderCallPopup({
    issuer,
    lenderCallDate,
    liveDomestic,
    liveInternational,
    livePasscode,
    replayDomestic,
    replayInternational,
    replayPasscode,
    onClose,
}: LenderCallPopupProps) {
    const popoverDate = moment.utc(lenderCallDate).format(constants.dateFormatDoubleDay);
    const popoverTime = moment.utc(lenderCallDate).format('h:mm A') + ' EST';

    const hasLive = liveDomestic || liveInternational || livePasscode;
    const hasReplay = replayDomestic || replayInternational || replayPasscode;

    return (
        <Popup
            title={
                <>
                    <span className="title">Lender Call</span>
                    <span className="name-tag text-ellipsis">{issuer}</span>
                </>
            }
            onClose={onClose}
            modalClass="modal-lender-call"
        >
            <PopupBody>
                <div className="lender-call-popup">
                    <InfoRow label="Date" value={popoverDate} />
                    <InfoRow label="Time (EST)" value={popoverTime} />
                    {hasLive && (
                        <>
                            <div className="lender-call-section-header">Live Dial-in</div>
                            <DialInRow label="Domestic" phone={liveDomestic} passcode={livePasscode} />
                            <DialInRow label="International" phone={liveInternational} passcode={livePasscode} />
                            {livePasscode && <InfoRow label="Passcode" value={livePasscode} />}
                        </>
                    )}
                    {hasReplay && (
                        <>
                            <div className="lender-call-section-header">Replay Dial-in</div>
                            <DialInRow label="Domestic" phone={replayDomestic} passcode={replayPasscode} />
                            <DialInRow label="International" phone={replayInternational} passcode={replayPasscode} />
                            {replayPasscode && <InfoRow label="Passcode" value={replayPasscode} />}
                        </>
                    )}
                </div>
            </PopupBody>
            <PopupFooter>
                <button className="btn btn-ghost" onClick={onClose}>Close</button>
            </PopupFooter>
        </Popup>
    );
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div className="lender-call-row">
            <span className="lender-call-row-label">{label}</span>
            <span className="lender-call-row-value">{value}</span>
        </div>
    );
}

function DialInRow({ label, phone, passcode }: { label: string; phone?: string; passcode?: string }) {
    if (!phone) return null;

    const dialLink = passcode ? `tel:${phone},,${passcode}#` : `tel:${phone}`;

    return (
        <div className="lender-call-row">
            <span className="lender-call-row-label">{label}</span>
            <a href={dialLink} className="link lender-call-row-value">{phone}</a>
        </div>
    );
}
